import { Component, ErrorInfo, ReactNode } from 'react';
import posthog from 'posthog-js';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

// App-wide error boundary. React only surfaces render-phase errors to class
// components, so this stays a class. When a render throws we report it to
// PostHog and show a recoverable fallback instead of a blank white screen
// (which is what users see today when something fails to render).
class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  // location.reload(true) is deprecated and ignored by modern browsers, so a
  // plain reload can re-serve the stale cached index.html that caused a chunk
  // error in the first place. Clearing CacheStorage and re-navigating with a
  // cache-busting query param guarantees a fresh fetch (fresh HTML → fresh
  // hashed chunks), including inside the Flutter WebView.
  hardReload = async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
    } catch {
      // cache clearing is best-effort; still reload below
    }
    const url = new URL(window.location.href);
    url.searchParams.set('reload', Date.now().toString());
    window.location.replace(url.toString());
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    // Surface in the console for local debugging and ship to PostHog so we can
    // see which device/build hit it.
    console.error('Render error caught by ErrorBoundary:', error, info);
    try {
      posthog.captureException(error, {
        componentStack: info.componentStack,
        location: window.location.href,
      });
    } catch {
      // never let error reporting throw inside the boundary
    }
  }

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-dark-bg text-dark-text">
        <div className="max-w-lg w-full text-center">
          <h1 className="text-3xl font-bold text-primary-500 mb-2">
            Something went wrong
          </h1>
          <p className="text-dark-muted mb-6">
            The app hit an unexpected error while loading. Reloading usually
            fixes it. If it keeps happening, the details below help us track it
            down.
          </p>

          <div className="flex gap-3 justify-center mb-6">
            <button
              className="btn btn-primary"
              onClick={this.hardReload}
            >
              Reload
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => {
                window.location.href = 'https://app.outclose.com';
              }}
            >
              Go home
            </button>
          </div>

          <details className="text-left bg-black/30 rounded-lg p-4 text-xs overflow-auto max-h-64">
            <summary className="cursor-pointer text-dark-muted mb-2">
              Error details
            </summary>
            <p className="font-mono text-primary-300 break-words whitespace-pre-wrap">
              {error.name}: {error.message}
            </p>
            {error.stack && (
              <pre className="mt-2 whitespace-pre-wrap break-words text-dark-muted">
                {error.stack}
              </pre>
            )}
            {componentStack && (
              <pre className="mt-2 whitespace-pre-wrap break-words text-dark-muted">
                {componentStack}
              </pre>
            )}
          </details>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
